<template>
    <div class="share-btn">
        <!-- If on mobile with web share API, show direct share button -->
        <navigator-share v-if="isItMobile && webShareApiSupported" :url="currentPath"
            :title="'Kiel.RefugeeCompass - Bildungs-, Beratungs- und Freizeitangebote für Geflüchtete in Kiel'">
            <template v-slot:clickable>
                <button class="btn btn-link" aria-label="Inhalt teilen">
                    <span class="material-icons-outlined" aria-hidden="true">share</span>
                </button>
            </template>
        </navigator-share>

        <!-- If not on mobile or web share API not supported, show overlay button -->
        <template v-else>
            <button @click.stop.prevent="toggleShareOptions" class="btn btn-link" :aria-expanded="showShareOptions"
                aria-label="Inhalt teilen" aria-haspopup="dialog">
                <span class="material-icons-outlined" aria-hidden="true">share</span>
            </button>

            <!-- Overlay backdrop -->
            <div v-if="showShareOptions" class="share-overlay-backdrop" @click.stop.prevent="toggleShareOptions"
                role="presentation"></div>

            <!-- Share options dialog -->
            <div v-if="showShareOptions" class="share-options-dialog" role="dialog" aria-labelledby="share-dialog-title"
                aria-modal="true" @click.stop.prevent>
                <h2 id="share-dialog-title" class="visually-hidden">Teilen-Optionen</h2>

                <button class="close-button" @click.stop.prevent="toggleShareOptions"
                    aria-label="Teilen-Dialog schließen">
                    <span class="material-icons-outlined">close</span>
                </button>

                <div class="share-options-list">

                    <button @click.stop.prevent="copyLink" class="share-option" aria-label="Link kopieren">
                        <span class="material-icons-outlined icon">link</span>
                        Link kopieren
                    </button>

                    <button @click.stop.prevent="shareViaEmail" class="share-option" aria-label="Per E-Mail teilen">
                        <span class="material-icons-outlined icon">email</span>
                        E-Mail
                    </button>

                    <button @click.stop.prevent="shareOnFacebook" class="share-option" aria-label="Auf Facebook teilen">
                        <span class="material-icons-outlined icon">facebook</span>
                        Facebook
                    </button>

                    <button @click.stop.prevent="shareOnWhatsApp" class="share-option" aria-label="Auf WhatsApp teilen">
                        <span class="material-icons-outlined icon">whatsapp</span>
                        WhatsApp
                    </button>

                    <button @click.stop.prevent="shareOnTelegram" class="share-option" aria-label="Auf Telegram teilen">
                        <span class="material-icons-outlined icon">telegram</span>
                        Telegram
                    </button>


                </div>
            </div>
        </template>
    </div>
</template>

<script>
import NavigatorShare from 'vue-navigator-share';
import { isMobile } from 'mobile-device-detect';

export default {
    name: "ShareButton",
    components: {
        NavigatorShare
    },
    data() {
        return {
            isItMobile: isMobile ? true : false,
            showShareOptions: false,
            lastFocusedElement: null
        }
    },
    computed: {
        currentPath() {
            return window.location.href;
        },
        webShareApiSupported() {
            return navigator && navigator.share;
        }
    },
    methods: {
        toggleShareOptions(event) {
            if (event) {
                event.preventDefault();
                event.stopPropagation();
            }

            if (!this.showShareOptions) {
                // Store the last focused element before opening the dialog
                this.lastFocusedElement = document.activeElement;
                // Prevent background scrolling
                document.body.style.overflow = 'hidden';
            } else {
                // Restore background scrolling
                document.body.style.overflow = '';
                // Restore focus to the last focused element
                if (this.lastFocusedElement) {
                    this.lastFocusedElement.focus();
                }
            }

            this.showShareOptions = !this.showShareOptions;

            if (this.showShareOptions) {
                // Focus the first share option when dialog opens
                this.$nextTick(() => {
                    const firstButton = this.$el.querySelector('.share-option');
                    if (firstButton) firstButton.focus();
                });
            }
        },
        handleKeydown(event) {
            if (event.key === 'Escape' && this.showShareOptions) {
                this.toggleShareOptions();
            } else if (event.key === 'Tab' && this.showShareOptions) {
                // Get all focusable elements in the dialog
                const focusableElements = this.$el.querySelectorAll(
                    '.share-option, .close-button'
                );
                const firstFocusable = focusableElements[0];
                const lastFocusable = focusableElements[focusableElements.length - 1];

                if (event.shiftKey) {
                    // If shift + tab and on first element, move to last element
                    if (document.activeElement === firstFocusable) {
                        event.preventDefault();
                        lastFocusable.focus();
                    }
                } else {
                    // If tab and on last element, move to first element
                    if (document.activeElement === lastFocusable) {
                        event.preventDefault();
                        firstFocusable.focus();
                    }
                }
            }
        },
        shareOnFacebook() {
            const url = encodeURIComponent(this.currentPath);
            window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}`, '_blank');
        },
        shareViaEmail() {
            const subject = encodeURIComponent(document.title || 'Shared Content');
            const body = encodeURIComponent(`Schau dir das an: ${document.title || 'Shared Content'}\n\n${this.currentPath}`);
            window.location.href = `mailto:?subject=${subject}&body=${body}`;
        },
        shareOnWhatsApp() {
            const text = encodeURIComponent(`${document.title || 'Shared Content'}\n${this.currentPath}`);
            const url = `https://wa.me/?text=${text}`;
            window.open(url, '_blank');
        },
        shareOnTelegram() {
            const url = encodeURIComponent(this.currentPath);
            const text = encodeURIComponent(document.title || 'Shared Content');
            window.open(`https://t.me/share/url?url=${url}&text=${text}`, '_blank');
        },
        shareOnSignal() {
            navigator.clipboard.writeText(`${document.title || 'Shared Content'} ${this.currentPath}`)
                .then(() => {
                    alert('Link für Signal in die Zwischenablage kopiert! Sie können ihn nun in Signal einfügen.');
                })
                .catch(err => {
                    console.error('Failed to copy: ', err);
                });
        },
        copyLink() {
            navigator.clipboard.writeText(this.currentPath)
                .then(() => alert('Link in die Zwischenablage kopiert!'))
                .catch(err => console.error('Link konnte nicht kopiert werden: ', err));
        }
    },
    mounted() {
        document.addEventListener('keydown', this.handleKeydown);
    },
    beforeDestroy() {
        document.removeEventListener('keydown', this.handleKeydown);
        // Ensure we restore scrolling when component is destroyed
        document.body.style.overflow = '';
    }
}
</script>

<style lang="scss" scoped>
@import '@/scss/_mixins.scss';
@import '@/scss/_variables.scss';

.share-btn {
    margin-left: 20px;
    position: relative;

    span {
        color: #fff;
        border: none;
        background: none;
    }

    .material-icons-outlined {
        color: #fff;
        padding: 10px;
        font-size: 24px;
        line-height: 1;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        @media (max-width: 991px) {
            padding: 0px;
        }

        &:hover {
            color: #CECECE;
        }
    }
}

.share-overlay-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1001;
    cursor: not-allowed;
}

.share-options-dialog {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: white;
    border-radius: 8px;
    padding: 20px;
    min-width: 280px;
    max-width: 90%;
    z-index: 1002;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    outline: none;

    .close-button {
        position: absolute;
        top: 10px;
        right: 10px;
        background: none;
        border: none;
        cursor: pointer;
        padding: 5px;
        color: #666;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            color: #333;
        }

        .material-icons-outlined {
            font-size: 20px;
            width: 20px;
            height: 20px;
            color: #666;
        }
    }

    .share-options-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-top: 20px;
    }

    .share-option {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        border: none;
        background: none;
        cursor: pointer;
        border-radius: 4px;
        transition: all 0.2s;
        color: #333;
        font-size: 16px;
        line-height: 1.5;
        width: 100%;

        &:hover {
            background-color: #f5f5f5;

            .icon {
                color: $primary;
            }
        }

        &:focus {
            outline: 2px solid $primary;
            outline-offset: 2px;
        }

        .icon {
            width: 24px;
            height: 24px;
            margin-right: 12px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: color 0.2s;
            font-size: 24px;
            color: #333;
        }
    }
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.share-btn .btn-link {
    color: #fff;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    background: none !important;
}

body[dir="rtl"] {
    .share-btn {
        margin-left: 0;
        margin-right: 20px;
    }

    .share-option .icon {
        margin-right: 0;
        margin-left: 12px;
    }
}
</style>